import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, ArrowLeft, Plus, Trash, ListBullets, Warning, Thermometer } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const emptyRow = () => ({ location: '', date: '', time: '' });

const BulkLookup = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([emptyRow(), emptyRow()]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    if (rows.length >= 10) {
      toast.error('Maximum 10 lookups per batch');
      return;
    }
    setRows((prev) => [...prev, emptyRow()]);
  };

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validRows = rows.filter((r) => r.location && r.date && r.time);
    if (validRows.length === 0) {
      toast.error('Please fill in at least one lookup');
      return;
    }
    setLoading(true);
    setResults(null);

    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/weather/bulk-lookup`,
        { lookups: validRows },
        { withCredentials: true }
      );
      setResults(data);
      toast.success(`Retrieved ${data.successful} of ${data.total} lookups`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'ERROR: Bulk lookup failed. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white mobile-nav-padding">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
            <Link to="/dashboard" data-testid="back-to-dashboard-link" className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-1.5">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tighter text-slate-950" style={{ fontFamily: 'Chivo' }}>Bulk Lookup</h1>
          <p className="text-sm text-slate-600 mt-2">Look up weather for multiple locations and dates at once (max 10)</p>
        </div>

        <form onSubmit={handleSubmit} className="mb-8">
          <div className="bg-white border border-slate-200">
            {/* Table header */}
            <div className="grid grid-cols-[1fr_1fr_1fr_48px] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Location</p>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Date</p>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Time</p>
              <p></p>
            </div>

            {/* Rows */}
            {rows.map((row, index) => (
              <div key={index} className="grid grid-cols-[1fr_1fr_1fr_48px] gap-4 px-6 py-3 border-b border-slate-100 items-center" data-testid={`bulk-row-${index}`}>
                <input
                  data-testid={`bulk-location-${index}`}
                  type="text"
                  value={row.location}
                  onChange={(e) => updateRow(index, 'location', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm"
                  placeholder="City, ZIP, or address"
                />
                <input
                  data-testid={`bulk-date-${index}`}
                  type="date"
                  value={row.date}
                  onChange={(e) => updateRow(index, 'date', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                />
                <input
                  data-testid={`bulk-time-${index}`}
                  type="time"
                  value={row.time}
                  onChange={(e) => updateRow(index, 'time', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  disabled={rows.length <= 1}
                  data-testid={`bulk-remove-${index}`}
                  className="p-2 text-slate-400 hover:text-red-600 disabled:opacity-30 transition-colors"
                >
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            ))}

            {/* Add row */}
            <div className="px-6 py-3">
              <button
                type="button"
                onClick={addRow}
                disabled={rows.length >= 10}
                data-testid="bulk-add-row-btn"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={16} weight="bold" />
                Add Row {rows.length < 10 && <span className="text-slate-400">({rows.length}/10)</span>}
              </button>
            </div>
          </div>

          <button
            data-testid="bulk-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full mt-6 px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <ListBullets size={20} weight="bold" />
            {loading ? 'Processing...' : `Run ${rows.filter((r) => r.location && r.date && r.time).length} Lookups`}
          </button>
        </form>

        {/* Results */}
        {results && (
          <div data-testid="bulk-results" className="space-y-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">
                Results ({results.successful}/{results.total} successful)
              </h2>
            </div>

            {/* Results table */}
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.2em] text-slate-500">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Date / Time</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Temp</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Humidity</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Wind</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Conditions</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((res, index) => {
                    const wd = res.weather_data?.weather_data || {};
                    const hasError = !!res.error;
                    return (
                      <tr key={index} className={`border-b border-slate-100 ${hasError ? 'bg-red-50' : ''}`} data-testid={`bulk-result-${index}`}>
                        <td className="px-4 py-3 font-mono text-slate-500">{index + 1}</td>
                        <td className="px-4 py-3 font-medium text-slate-950">{res.input?.location || res.location}</td>
                        <td className="px-4 py-3 font-mono text-slate-700">{res.input?.date} {res.input?.time}</td>
                        {hasError ? (
                          <td colSpan={4} className="px-4 py-3 text-red-600 text-xs">{res.error}</td>
                        ) : (
                          <>
                            <td className="px-4 py-3 text-right font-mono font-bold text-slate-950">{wd.temp ?? 'N/A'}°F</td>
                            <td className="px-4 py-3 text-right font-mono text-slate-950">{wd.humidity ?? 'N/A'}%</td>
                            <td className="px-4 py-3 text-right font-mono text-slate-950">{wd.windspeed ?? 'N/A'} mph</td>
                            <td className="px-4 py-3 text-slate-950">{wd.conditions || 'N/A'}</td>
                          </>
                        )}
                        <td className="px-4 py-3">
                          {hasError ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-600 border border-red-200 text-xs font-mono">FAILED</span>
                          ) : res.weather_data?.warning ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 text-xs font-mono">LIMITED</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 border border-emerald-200 text-xs font-mono">LIVE</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-8 p-4 border border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-600 leading-relaxed">
            <strong className="text-slate-900">DISCLAIMER:</strong> This data is generated using historical weather data from third-party data providers. While data is sourced from reputable services, accuracy is not guaranteed.
          </p>
        </div>
      </main>
    </div>
  );
};

export default BulkLookup;
